import * as config from '@/config/env.config';
import { getTokenFromLocalStorage } from '@/utils/auth0Token';
import { getIsLoggingOut } from '@/utils/logoutState';
import { clearSelectedProjectStorage } from '@/utils/clearSelectedProjectStorage';
import { BUILD_A_STUDY } from '@/views/routes';

// Pages where a 401/403 is the expected terminal state. Auth0 redirect Actions land the user on
// these with a session but no access token, so redirecting away bounces straight back to
// /authorize, which re-runs the Action and re-lands here — an unbreakable loop.
const NO_AUTH_REDIRECT_PATHS = new Set([
	'/',
	'/login',
	'/callback',
	'/securitycheck',
	'/security',
	'/verifyemail',
	'/learning-center/access-denied',
	`/home/${BUILD_A_STUDY}`,
]);

// Routes are not `strict`, so /verifyemail/ resolves and must match the set too.
const normalizePath = (pathname) => pathname.replace(/\/+$/, '') || '/';

let authRedirectInProgress = false;

/** @internal test-only */
export const __resetAuthRedirectForTests = () => {
	authRedirectInProgress = false;
};

/**
 * Checks if the user is authenticated according to Auth0 cache
 * This checks the Auth0 cache in localStorage to determine authentication state
 * @returns {boolean} True if user appears to be authenticated, false otherwise
 */
const isUserAuthenticated = () => {
	if (typeof window === 'undefined') {
		return false;
	}

	// Only treat a user as authenticated when a non-expired token exists.
	return !!getTokenFromLocalStorage();
};

const scheduleAuthRedirect = (path) => {
	if (authRedirectInProgress || typeof window === 'undefined') {
		return;
	}
	authRedirectInProgress = true;
	// Drop stale project selection before /securitycheck so "Try Again" cannot re-query a revoked project.
	if (path === '/securitycheck') {
		clearSelectedProjectStorage();
	}
	window.location.replace(path);
};

/**
 * Centralized 401/403 error handler
 * Redirects to /login for missing sessions or /securitycheck for authorization failures
 *
 * This is used by:
 * - RTK Query APIs (baseQueryWithAuth)
 * - Legacy middleware functions (get, post, put, callApi, etc.)
 *
 * Strategy:
 * - For 401 from the API when a token was present: redirect to /securitycheck (token refresh
 *   already ran in baseQuery; the API rejected the session).
 * - For 401 before a request (no token available): redirect to /login.
 * - For 401 with no token: redirect to /login.
 * - For 403 errors: Redirect to /securitycheck when user appears authenticated.
 * - No redirect at all from NO_AUTH_REDIRECT_PATHS, where 401/403 is the expected state.
 *
 * @param {number|string} status - HTTP status code
 * @param {Object} errorData - Optional error data for logging
 * @param {string} endpoint - Optional endpoint for logging
 * @param {{ preRequest?: boolean }} options - preRequest: synthetic 401 before HTTP call
 */
export const handleAuthError = async (status, errorData = null, endpoint = null, options = {}) => {
	// Only handle 401 (Unauthorized) and 403 (Forbidden)
	if (status !== 401 && status !== 403) {
		return;
	}

	if (typeof window === 'undefined') {
		return;
	}

	const currentPath = normalizePath(window.location.pathname);
	const isLoggingOut = getIsLoggingOut();
	if (isLoggingOut) {
		return;
	}

	if (NO_AUTH_REDIRECT_PATHS.has(currentPath)) {
		return;
	}

	if (status === 401) {
		// Client-side guard: no token was sent; send user to login.
		if (options.preRequest) {
			scheduleAuthRedirect('/login');
			return;
		}

		// API rejected the request. If we still have a token, this is a portal/session issue
		// (not fixable by another silent refresh) — show the error page instead of looping on /login.
		if (isUserAuthenticated()) {
			scheduleAuthRedirect('/securitycheck');
		} else {
			scheduleAuthRedirect('/login');
		}
		return;
	}

	// For 403, check if user is still authenticated according to Auth0 cache.
	// If not authenticated, the 403 is expected and we avoid redirect churn.
	const isAuthenticated = isUserAuthenticated();
	if (!isAuthenticated) {
		return;
	}

	const errorInfo = {
		status,
		message: errorData?.message || errorData?.error || 'Unauthorized/Forbidden',
		endpoint: endpoint || 'unknown',
		url: window.location.href,
		pathname: currentPath,
		userAuthenticated: true
	};

	if (config.ENABLE_SECURITY_ALERT) {
		alert('About to redirect to /securitycheck\n\nState:\n- Status: ' + status + '\n- Error: ' + JSON.stringify(errorInfo, null, 2) + '\n\nCheck console and state before clicking OK');
	}

	scheduleAuthRedirect('/securitycheck');
};

/**
 * Checks a fetch Response for 401/403 errors and handles them
 * @param {Response} response - Fetch Response object
 * @param {string} endpoint - Optional endpoint for logging
 * @returns {boolean} - True if error was handled (redirect initiated)
 */
export const handleResponseAuthError = async (response, endpoint = null) => {
	if (!response || !response.ok) {
		const status = response?.status;
		if (status === 401 || status === 403) {
			let errorData = null;
			try {
				errorData = await response.json().catch(() => ({}));
			} catch (e) {
				// If JSON parsing fails, use status text
				errorData = { error: response.statusText || 'Unknown error' };
			}
			await handleAuthError(status, errorData, endpoint);
			return true; // Error was handled
		}
	}
	return false; // No error or not a 401/403
};
